<template>
  <v-container fluid>
    <SearchBar v-model="search" />

    <v-card outlined class="rounded-xl mt-5">
      <ListTitle
        :title="$t('pageHeader.employee')"
        @new="$router.push({ name: 'employee-create' })"
      />

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="employees"
          :server-items-length="count"
          :loading="loading"
          :options.sync="options"
          :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
          class="table-header"
        >
          <template v-slot:item="{ item, index }">
            <tr>
              <td>#{{ index + 1 }}</td>

              <td>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs">
                      {{ item.createdAt | moment("L") }}
                    </span>
                  </template>
                  <span>{{ item.createdAt | moment("L") }}</span>
                </v-tooltip>
              </td>

              <td class="text-xs-left small-td">{{ item.firstName }}</td>
              <td class="text-xs-left small-td">{{ item.lastName }}</td>

              <td class="text-xs-left">
                {{ item.phoneNumber || "N/A" }}
              </td>

              <!-- NEW: Active Locations + Roles -->
              <td class="text-xs-left medium-td" style="white-space: pre-line">
                {{ formatActiveLocations(item.ActiveLocations) }}
              </td>
              <td class="text-center px-0">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item @click="editEmployee(item)">
                      <v-list-item-title> Edit </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!LocationId"
                      @click="deleteEmployee(item)"
                    >
                      <v-list-item-title>
                        {{ $t("buttons.delete") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import employeeService from "../service";

export default {
  name: "employee-list",
  props: ["LocationId", "hideHeader", "hideCreate"],

  data() {
    return {
      search: "",
      employees: [],
      count: 0,
      loading: false,
      rowsPerPageItems: [10, 25, 50, 100],

      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["activeLocationSort"], // default sort by first location name
        sortDesc: [false],
      },

      headers: [
        { text: "#" },
        { text: this.$t("labels.createdAt"), value: "createdAt" },
        { text: this.$t("labels.firstName"), value: "firstName" },
        { text: this.$t("labels.lastName"), value: "lastName" },
        { text: this.$t("labels.phoneNumber"), value: "phoneNumber" },

        // NEW COLUMN
        { text: "Active In Locations", value: "activeLocationSort" },

        { text: "", value: "actions", sortable: false },
      ],
    };
  },

  watch: {
    search() {
      if (this.search.length > 3 || this.search.length === 0) {
        this.getList();
      }
    },
    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },

  created() {
    this.getList();
  },

  methods: {
    /* Format Active Locations into readable string */
    formatActiveLocations(locations) {
      if (!locations || !locations.length) return "None";

      return locations
        .map((loc) => {
          const locationName = loc.Location?.name || "Unnamed Location";
          const roleName = loc.EmployeeRole?.name || "No Role";
          return `${locationName} (${roleName})`;
        })
        .join("\n"); // NEW → line break instead of comma
    },

    async getList() {
      this.loading = true;

      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const offset = (page - 1) * itemsPerPage;
      const limit = itemsPerPage;
      const orderBy = [sortBy[0] || "firstName", sortDesc[0] || false];

      try {
        const response = await employeeService.getAll({
          limit,
          offset,
          search: this.search,
          local: true,
          orderBy,
          LocationId: this.LocationId,
          includeRole: true,
        });

        // ⭐ Add sortable field for new column
        this.employees = response.data.map((emp) => {
          const firstLoc = emp.ActiveLocations?.[0];
          return {
            ...emp,
            activeLocationSort: firstLoc?.name || "", // used for sorting
          };
        });

        this.count = response.count;
      } finally {
        this.loading = false;
      }
    },

    editEmployee(employee) {
      this.$router.push({
        name: "employee-edit",
        params: { Employee: employee, LocationId: this.LocationId },
      });
    },

    deleteEmployee(employee) {
      this.$swal({
        title: this.$t("messages.title.areYouSure"),
        text: this.$t("messages.text.youWontBeAbleToRevert"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: this.$t("buttons.cancel"),
        confirmButtonText: this.$t("buttons.yesDeleteIt"),
      }).then((result) => {
        if (result.value) {
          employeeService.delete(employee.id).then((response) => {
            if (response.data) {
              this.$swal(
                this.$t("messages.title.deleted"),
                this.$t("messages.text.employeeHasDelete"),
                "success"
              );
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.medium-td {
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>